<template>
  <fin-data-loader :resolver="items" @input="loaded" overlay ref="loader" :params="dataLoaderParams">
    <template #content="{data}">
      <slot name="aboveChartActions"/>

      <div v-if="selectedItem">
        <v-row v-if="!listOnly">
          <v-col :cols="noPreview ? 12 : data.length > 1 && data.length <= 4 ? 8 : 12" class="py-0">
            <v-carousel v-model="model"
                        :show-arrows="data.length > 1"
                        height="auto"
                        hide-delimiters>
              <v-carousel-item
                v-for="(item, index) in data"
                :key="index"
              >
                <component :is="detailOptions.type({data, item: item, selected: true})"
                           ref="detailChart"
                           v-bind="detailOptions.props({data, item: item, selected: true})"
                           v-on="item.onDetail">
                  <template #card-actions>
                    <slot name="card-actions" :item="item"/>
                  </template>
                </component>
              </v-carousel-item>
            </v-carousel>
          </v-col>

          <template v-if="!noPreview">
            <!-- if there are more than four items => show one big card + scroll carousel -->
            <v-col v-if="data.length > 4">
              <fin-scroll-carousel :items="data" v-model="model">
                <template #default="{item, index}">
                  <component
                    :is="previewOptions.type({data, item, isRight: false, selected: selectedItem.id === item.id})"
                    class="mr-2 mt-5"
                    style="width: 270px; max-width: 270px;"
                    v-bind="previewOptions.props({data, item, isRight: false, selected: selectedItem.id === item.id})"
                    @click="cardChange(item, index)">
                    <template #small-chart-actions>
                      <slot :item="item" name="small-chart-actions"/>
                    </template>
                  </component>
                </template>
              </fin-scroll-carousel>
            </v-col>

            <!-- if there are up to four items => show one big card on the left and four small card on the right -->
            <v-col v-else-if="data.length > 1" cols="4" class="py-0">
              <component :is="previewOptions.type({data, item, isRight: true, selected: selectedItem.id === item.id})"
                         v-for="(item, index) in data"
                         :key="item.id"
                         class="mb-2"
                         v-bind="previewOptions.props({data, item, isRight: true, selected: selectedItem.id === item.id})"
                         @click="cardChange(item, index)">
                <template #small-chart-actions>
                  <slot :item="item" name="small-chart-actions"/>
                </template>
              </component>
            </v-col>
          </template>
        </v-row>

        <v-row v-else>
          <v-col>
            <fin-scroll-carousel :items="data">
              <template #default="{item}">
                <component :is="detailOptions.type({data, item, selected: false})"
                           class="mr-2 fill-height"
                           style="width: 310px; max-width: 310px;"
                           v-bind="detailOptions.props({data, item, selected: false})"
                           v-on="item.onDetail">
                  <template #card-actions>
                    <slot name="card-actions" :item="item"/>
                  </template>
                </component>
              </template>
            </fin-scroll-carousel>
          </v-col>
        </v-row>
      </div>
      <slot v-else name="noData">Keine Daten</slot>
    </template>
  </fin-data-loader>
</template>

<script>
import finScrollCarousel from '@/components/common/ScrollCarousel'
import FinBigPerformanceChart from "@/components/common/Chart2/PerformanceCharts/BigPerformanceChart"
import BigPerformanceChart from "@/components/common/Chart2/PerformanceCharts/BigPerformanceChart"
import FinSmallPerformanceChart from "@/components/common/Chart2/PerformanceCharts/SmallPerformanceChart"
import FinDataLoader from "@/components/common/DataLoader"
import AmplitudePush from "@/mixins/AmplitudePush"

export default {
  name: 'FinChartList',
  mixins: [AmplitudePush],
  components: {
    FinDataLoader,
    FinSmallPerformanceChart,
    FinBigPerformanceChart,
    finScrollCarousel
  },
  props: {
    items: {
      type: [Array, Object, Function]
    },
    dataLoaderParams: {
      type: Object
    },
    listOnly: {
      type: Boolean
    },
    noPreview: {
      type: Boolean
    },
    detailOptions: {
      type: Object,
      default: () => ({
        type: () => BigPerformanceChart,
        props: ({item}) => ({
          title: item.titleDetail || item.title,
          currentValueTitle: item.currentValueTitle,
          chartOptions: item.chart,
          tradingPlatform: item.tradingPlatform,
          prependPerformanceText: item.prependPerformanceText,
          hidePercentageChange: item.hidePercentageChange,
          currentValueUseSemanticColors: item.currentValueUseSemanticColors,
          inactiveItem: item.inactiveItem,
          to: item.to,
          compareTitle: item.compareTitle
        })
      })
    },
    previewOptions: {
      type: Object,
      default: () => ({
        type: () => FinSmallPerformanceChart,
        props: ({item, isRight, selected}) => ({
          titleFirst: !isRight,
          cardOptions: {[item.highlightCard ? 'highlightCard' : isRight ? 'leftArrow' : 'topArrow']: selected},
          title: item.title,
          chartOptions: item.previewChart,
          hidePercentageChange: item.hidePercentageChange,
          inactiveItem: item.inactiveItem,
        })
      })
    },
  },
  data() {
    return {
      item: null,
      loadedItems: null,
      selectedItem: undefined,
      selectedGroup: null,
      model: 0
    }
  },
  methods: {
    cardChange(item, index) {
      this.model = index
    },
    loaded(items) {
      this.loadedItems = items

      if (this.selectedItem && this.loadedItems) {
        const {id} = this.selectedItem
        const oldModel = this.model

        // find new index of previously selected item
        const index = this.loadedItems.findIndex(item => item.id === id)
        this.model = index !== -1 ? index : 0

        // manually trigger item selection since watch doesnt trigger in this case
        if (oldModel === this.model) {
          this.changeSelectedItemToIndex(this.model)
        }
      } else {
        this.model = null
      }

      this.$emit('input', items)
    },
    async reload() {
      await this.$refs.loader._fetchData()
    },
    reflowChart(val, timeout = 0) {
      this.$nextTick(() => {
        const chart = this.$refs.detailChart?.[val]?.$refs?.chart
        if (chart) {
          //chart was maybe rendert before with a different size and is shown now again
          setTimeout(chart.reflow, timeout)
        }
      })
    },
    changeSelectedItemToIndex(val) {
      this.selectedItem = this.loadedItems[val || 0]
      this.$emit("selectedItem", this.selectedItem)
      this.reflowChart(val, 300)
    }
  },
  watch: {
    model(val) {
      this.selectedItem = this.loadedItems[val || 0]
      this.$emit("selectedItem", this.selectedItem)
      this.reflowChart(val, 300)
    },
    noPreview() {
      this.reflowChart(this.model)
    },
    listOnly() {
      this.reflowChart(this.model)
    },
  }
}
</script>

<style scoped lang="scss">

html {
  /*
  https://stackoverflow.com/questions/41204581/iphone-negative-margin-causes-position-drift-in-slideout-menu
  Solution in order to display the carousels' arrows properly on iOS in the dashboard/portfolios, dashboard/marktentwicklung.
  Without this fix, the arrows appear on the texts in iOS.
  */
  overflow-x: hidden;
  overflow-y: scroll;
}

::v-deep {
  .v-window__next, .v-window__prev {
    min-width: 26px !important;
    background-color: transparent;

    .v-icon {
      font-size: 40px;
      color: $fin-color-primary-dark !important;
    }
  }

  .v-window__prev {
    left: -25px;
  }

  .v-window__next {
    right: -25px;
  }
}

</style>
